import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Website v2 comes in two editions (HOF-662). `founders` is the site as
 * designed: both founders, with their photos. `ceo` names and shows Nicole
 * only. An edition's own copy lives under `homeV2.editions.<edition>` and
 * overrides the shared `homeV2` key of the same path.
 */
export type Edition = 'founders' | 'ceo';

/**
 * The edition served as the homepage at `/`. HOF-662: switches to `founders`
 * on 01.11.2026, and that switch is this one line.
 */
export const HOMEPAGE_EDITION: Edition = 'ceo';

export interface Site {
  edition: Edition;
  /** Path prefix of the site's pages: `/home_v2` for the draft, empty at the root. */
  base: '' | '/home_v2';
  /** A draft beside the live site carries `noindex`. */
  draft: boolean;
}

/** The draft at /home_v2, kept exactly as it was designed. */
export const DRAFT_SITE: Site = { edition: 'founders', base: '/home_v2', draft: true };

/** The homepage at `/`, `/unternehmen` and `/sicherheit`. */
export const HOMEPAGE_SITE: Site = { edition: HOMEPAGE_EDITION, base: '', draft: false };

const SiteContext = createContext<Site>(DRAFT_SITE);

export const SiteProvider = SiteContext.Provider;

export const useSite = (): Site => useContext(SiteContext);

/** Resolves a `homeV2` copy key to the edition's own wording where it has one. */
export function useEditionKey(): (key: string) => string {
  const { edition } = useSite();
  const { i18n } = useTranslation();
  return (key) => {
    const own = `homeV2.editions.${edition}.${key}`;
    return i18n.exists(own) ? own : `homeV2.${key}`;
  };
}
